import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { showNotification } from '@mantine/notifications';
import { QUERY_KEYS } from '@/constants/queryKeys';
import { churchAdminAccessService } from '@/services/church-admin-access.service';
import type {
  ChurchAdminAccessListParams,
  GrantChurchAdminAccessPayload,
  InitTempChurchAdminAccessPayload,
} from '@/types';

export const useChurchAdminAccess = (params: ChurchAdminAccessListParams = {}) => {
  return useQuery({
    queryKey: [QUERY_KEYS.CHURCH_ADMIN_ACCESS, params],
    queryFn: () => churchAdminAccessService.list(params),
  });
};

export const useGrantChurchAdminAccessMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      churchId,
      userId,
      payload,
    }: {
      churchId: string;
      userId: string;
      payload: GrantChurchAdminAccessPayload;
    }) => churchAdminAccessService.grantChurchAccess({ churchId, userId, payload }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.CHURCH_ADMIN_ACCESS] });
      showNotification({ title: 'Success', message: 'Church access granted successfully', color: 'green' });
    },
  });
};

export const useRemoveChurchAdminAccessMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ churchId, userId }: { churchId: string; userId: string }) =>
      churchAdminAccessService.removeChurchAccess({ churchId, userId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.CHURCH_ADMIN_ACCESS] });
      showNotification({ title: 'Success', message: 'Church access removed successfully', color: 'green' });
    },
  });
};

export const useInitTempChurchAdminAccessMutation = () => {
  return useMutation({
    mutationFn: (payload: InitTempChurchAdminAccessPayload) => churchAdminAccessService.initTempAccess(payload),
  });
};
